import {
  ChevronDown,
  X,
  RotateCcw,
  Search,
  Book,
  FileText,
  Code,
  Calculator,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

import ErrorMessage from '../components/Common/ErrorMessage';
import Loading from '../components/Common/Loading';
import NoDataMessage from '../components/Common/NoDataMessage';
import {
  QuestionBankQuestion,
  QuestionData
} from '../redux/Slices/QuestionBankSlice/types';
import {
  useGetQuestionBankDataQuery,
  useGetQuestionBankFilterMutation
} from '../services/questionBankApi';
import { renderHtmlWithTeX } from '../utils/RenderQuestionWithTex';

// Type definitions
type Topic = {
  topicId: string;
  topicName: string;
};

export type QuestionBankSection = {
  sectionId: string;
  sectionName: string;
  topics: Topic[];
};

type FilterData = {
  pageNumber: number;
  pageSize: number;
  sectionIds: string[];
  topicIds: string[];
};

// Reusable Accordion Component
type AccordionProps = {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
  badge?: string;
};

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  isOpen,
  onToggle,
  icon,
  badge
}) => {
  return (
    <div className='overflow-hidden rounded-lg border border-gray-200 shadow-sm'>
      <button
        onClick={onToggle}
        className='flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors duration-200 hover:bg-gray-100'
      >
        <div className='flex items-center space-x-3'>
          {icon}
          <span className='font-medium text-gray-900'>{title}</span>
          {badge && (
            <span className='rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800'>
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className='border-t border-gray-200 bg-white'>{children}</div>
      )}
    </div>
  );
};

// Multi-Select Dropdown Component
type MultiSelectProps = {
  options: { id: string; name: string }[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  setQuestionData?: (questionData: QuestionData | null) => void;
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onSelectionChange,
  placeholder,
  disabled = false,
  setQuestionData
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOption = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const removeSelection = (id: string) => {
    if (setQuestionData) {
      setQuestionData(null);
    }
    onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
  };

  const getSelectedNames = () => {
    return options
      .filter(option => selectedIds.includes(option.id))
      .map(option => option.name);
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <div
        className={`min-h-[44px] cursor-pointer rounded-lg border border-gray-300 p-3 transition-colors duration-200 ${
          disabled
            ? 'cursor-not-allowed bg-gray-100'
            : 'bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 hover:border-gray-400'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className='flex flex-wrap gap-1'>
          {selectedIds.length === 0 ? (
            <span className='text-gray-500'>{placeholder}</span>
          ) : (
            getSelectedNames().map((name, index) => (
              <span
                key={name}
                className='inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800'
              >
                {name}
                {!disabled && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      const option = options.find(opt => opt.name === name);
                      if (option) removeSelection(option.id);
                    }}
                    className='ml-1 hover:text-blue-600'
                  >
                    <X className='h-3 w-3' />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <ChevronDown
          className={`absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {isOpen && !disabled && (
        <div className='absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
          {options.length === 0 ? (
            <div className='px-3 py-2 text-center text-gray-500'>
              No options available
            </div>
          ) : (
            options.map(option => (
              <div
                key={option.id}
                className={`cursor-pointer px-3 py-2 transition-colors duration-150 hover:bg-gray-100 ${
                  selectedIds.includes(option.id)
                    ? 'bg-blue-50 text-blue-700'
                    : ''
                }`}
                onClick={() => handleToggleOption(option.id)}
              >
                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedIds.includes(option.id)}
                    onChange={() => {
                      // eslint-disable-next-line no-param-reassign
                    }}
                    className='mr-2'
                  />
                  <span>{option.name}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Individual Question Accordion Component
type QuestionAccordionProps = {
  question: QuestionBankQuestion;
  questionNumber: number;
  sectionsData: QuestionBankSection[];
  isOpen: boolean;
  onToggle: () => void;
};

const QuestionAccordion: React.FC<QuestionAccordionProps> = ({
  question,
  questionNumber,
  sectionsData,
  isOpen,
  onToggle
}) => {
  const getSectionIcon = (sectionId: string) => {
    switch (sectionId) {
      case '67f4dc9612b8026f68aca586':
        return <Calculator className='h-5 w-5 text-blue-600' />;
      case '67f4dcba12b8026f68aca587':
        return <Book className='h-5 w-5 text-green-600' />;
      case '67f4dce012b8026f68aca588':
        return <Code className='h-5 w-5 text-purple-600' />;
      case '67f4dcf912b8026f68aca589':
        return <FileText className='h-5 w-5 text-orange-600' />;
      default:
        return <FileText className='h-5 w-5 text-gray-600' />;
    }
  };

  const getSectionName = (sectionId: string) => {
    const section = sectionsData.find(s => s?.sectionId === sectionId);
    return section?.sectionName || 'Unknown Section';
  };

  const getTopicName = (topicId: string) => {
    for (const section of sectionsData) {
      const topic = section?.topics?.find(t => t?.topicId === topicId);
      if (topic) return topic.topicName;
    }
    return 'Unknown Topic';
  };

  const questionPreview =
    question.questionText.length > 100
      ? `${question.questionText.substring(0, 100)}...`
      : question.questionText;

  return (
    <div className='mb-3 overflow-hidden rounded-lg border border-gray-200 shadow-sm'>
      <button
        onClick={onToggle}
        className='flex w-full items-start justify-between bg-gray-50 p-4 text-left transition-colors duration-200 hover:bg-gray-100'
      >
        <div className='flex-1'>
          <div className='mb-2 flex items-center space-x-2'>
            <span className='flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-800'>
              {questionNumber}
            </span>
            {getSectionIcon(question.sectionId)}
            <div className='text-sm text-gray-600'>
              <span className='font-medium'>
                {getSectionName(question.sectionId)}
              </span>
              <span className='mx-1'>•</span>
              <span>{getTopicName(question.topicId)}</span>
              {question.subTopic && (
                <>
                  <span className='mx-1'>•</span>
                  <span className='text-blue-600'>{question.subTopic}</span>
                </>
              )}
            </div>
          </div>
          <div className='text-sm text-gray-800'>
            {isOpen
              ? renderHtmlWithTeX(question.questionText, true)
              : renderHtmlWithTeX(questionPreview, true)}
          </div>
        </div>
        <ChevronDown
          className={`ml-4 h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className='border-t border-gray-200 bg-white p-4'>
          <div className='mb-4'>
            <p className='font-medium leading-relaxed text-gray-900'>
              {renderHtmlWithTeX(question.questionText, true)}
            </p>
          </div>

          <div className='mb-4 grid grid-cols-1 gap-2 md:grid-cols-2'>
            {question?.options?.map((option, index) => (
              <div
                key={option?.correctAnswer || index}
                className={`rounded-lg border p-3 transition-colors duration-150 ${
                  option.isCorrectAnswer
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-gray-200 bg-gray-50 text-gray-700'
                }`}
              >
                <div className='flex items-center'>
                  <span className='mr-2 font-medium'>
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span>{renderHtmlWithTeX(option?.answerOption, true)}</span>
                  {option.isCorrectAnswer && (
                    <span className='ml-auto text-sm font-medium text-green-600'>
                      ✓ Correct
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {question.explanation && (
            <div className='rounded-lg border border-blue-200 bg-blue-50 p-3'>
              <p className='text-sm text-blue-800'>
                <span className='font-medium'>Explanation: </span>
                {renderHtmlWithTeX(question?.explanation, true)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Pagination Component
type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className='flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6'>
      <div className='flex flex-1 justify-between sm:hidden'>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className='relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className='relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Next
        </button>
      </div>
      <div className='hidden sm:flex sm:flex-1 sm:items-center sm:justify-between'>
        <div>
          <p className='text-sm text-gray-700'>
            Page <span className='font-medium'>{currentPage}</span> of{' '}
            <span className='font-medium'>{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className='isolate inline-flex -space-x-px rounded-md shadow-sm'>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className='relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <ChevronLeft className='h-5 w-5' />
            </button>

            {getPageNumbers().map((page, index) => (
              <React.Fragment key={`${page}-${index + 1}`}>
                {page === '...' ? (
                  <span className='relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300'>
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => onPageChange(page as number)}
                    disabled={isLoading}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 ${
                      currentPage === page
                        ? 'z-10 bg-blue-600 text-white focus-visible:outline-blue-600'
                        : 'text-gray-900'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className='relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <ChevronRight className='h-5 w-5' />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Main Component
const QuestionBankV2: React.FC = () => {
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [questionData, setQuestionData] = useState<QuestionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [questionsAccordionOpen, setQuestionsAccordionOpen] = useState(true);
  const [openQuestionIds, setOpenQuestionIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { currentData: sectionsData } = useGetQuestionBankDataQuery();
  const [getQuestionBankFilter, { isError }] =
    useGetQuestionBankFilterMutation();

  // Get available topics based on selected sections
  const getAvailableTopics = (): { id: string; name: string }[] => {
    if (selectedSectionIds.length === 0) return [];

    const topics: { id: string; name: string }[] = [];
    selectedSectionIds.forEach(sectionId => {
      const section = sectionsData?.data?.sections?.find(
        s => s.sectionId === sectionId
      );
      if (section) {
        section.topics.forEach(topic => {
          topics.push({ id: topic.topicId, name: topic.topicName });
        });
      }
    });
    return topics;
  };

  // Clear topics when no sections are selected
  useEffect(() => {
    if (selectedSectionIds.length === 0) {
      setSelectedTopicIds([]);
    } else {
      // Remove topics that are no longer available
      const availableTopicIds = getAvailableTopics().map(t => t.id);
      setSelectedTopicIds(prev =>
        prev.filter(id => availableTopicIds.includes(id))
      );
    }
  }, [selectedSectionIds]);

  const handleClearFilters = () => {
    setSelectedSectionIds([]);
    setSelectedTopicIds([]);
    setQuestionData(null);
    setCurrentPage(1);
    setOpenQuestionIds(new Set());
  };

  const fetchQuestions = async (page = 1) => {
    setIsLoading(true);

    const filterData: FilterData = {
      pageNumber: page - 1,
      pageSize,
      sectionIds: selectedSectionIds,
      topicIds: selectedTopicIds
    };

    try {
      console.log('Filter Data:', filterData);
      const result = await getQuestionBankFilter(filterData);
      setQuestionData(result.data);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    setCurrentPage(1);
    setOpenQuestionIds(new Set());
    fetchQuestions(1);
    setQuestionsAccordionOpen(true);
  };

  const handlePageChange = (page: number) => {
    fetchQuestions(page);
    setOpenQuestionIds(new Set()); // Close all questions when changing page
  };

  const toggleQuestionAccordion = (questionId: string) => {
    setOpenQuestionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const sectionOptions = sectionsData?.data?.sections?.map(section => ({
    id: section.sectionId,
    name: section.sectionName
  }));

  const isSubmitDisabled = selectedSectionIds.length === 0;

  const renderContent = () => {
    if (isLoading) return <Loading text='Loading...' />;
    if (isError) return <ErrorMessage />;
    if (questionData?.data?.length === 0) {
      return (
        <NoDataMessage
          icon='search'
          title='No questions found'
          message='Please select sections and topics to view questions.'
        />
      );
    } else if (questionData?.data && questionData?.data?.length > 0) {
      return questionData?.data?.map((question, index) => (
        <QuestionAccordion
          key={question.id}
          question={question}
          questionNumber={(currentPage - 1) * pageSize + index + 1}
          sectionsData={sectionsData?.data?.sections || []}
          isOpen={openQuestionIds.has(question.id)}
          onToggle={() => toggleQuestionAccordion(question.id)}
        />
      ));
    }
    if (!isLoading) return <Loading text='Loading...' />;
    if (isError) return <ErrorMessage />;
  };

  return (
    <div className='mx-auto min-h-screen max-w-6xl bg-gray-50 p-6'>
      <div className='rounded-xl bg-white shadow-lg'>
        {/* Header */}
        <div className='border-b border-gray-200 p-6'>
          <h1 className='mb-2 text-3xl font-bold text-gray-900'>
            Question Bank
          </h1>
          <p className='text-gray-600'>
            Select sections first, then choose specific topics to filter and
            view questions.
          </p>
        </div>

        {/* Filters */}
        <div className='border-b border-gray-200 bg-gray-50 p-6'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {/* Section Dropdown */}
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-700'>
                Select Sections *
              </label>
              <MultiSelect
                options={sectionOptions || []}
                selectedIds={selectedSectionIds}
                onSelectionChange={setSelectedSectionIds}
                placeholder='Choose sections...'
                setQuestionData={setQuestionData}
              />
              {selectedSectionIds.length > 0 && (
                <p className='mt-1 text-sm text-gray-600'>
                  {selectedSectionIds.length} section(s) selected
                </p>
              )}
            </div>

            {/* Topic Dropdown */}
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-700'>
                Select Topics
              </label>
              {selectedSectionIds.length === 0 ? (
                <div className='rounded-lg border border-gray-300 bg-gray-100 p-3 text-center text-gray-500'>
                  Please select sections first
                </div>
              ) : (
                <>
                  <MultiSelect
                    options={getAvailableTopics()}
                    selectedIds={selectedTopicIds}
                    onSelectionChange={setSelectedTopicIds}
                    placeholder='Choose topics...'
                  />
                  <p className='mt-1 text-sm text-gray-600'>
                    {getAvailableTopics().length} topic(s) available,{' '}
                    {selectedTopicIds.length} selected
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className='mt-6 flex flex-col gap-3 sm:flex-row'>
            <button
              onClick={handleClearFilters}
              className='flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-100'
              disabled={
                selectedSectionIds.length === 0 && selectedTopicIds.length === 0
              }
            >
              <RotateCcw className='mr-2 h-4 w-4' />
              Clear Filters
            </button>

            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isLoading}
              className={`flex items-center justify-center rounded-lg px-6 py-2 font-medium transition-colors duration-200 ${
                isSubmitDisabled || isLoading
                  ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
                  Loading...
                </>
              ) : (
                <>
                  <Search className='mr-2 h-4 w-4' />
                  Search Questions
                  {selectedSectionIds.length > 0 && (
                    <span className='ml-2 rounded-full bg-blue-500 px-2 py-1 text-xs'>
                      {selectedSectionIds.length + selectedTopicIds.length}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Questions Section */}
        {questionData && (
          <div className='p-6'>
            <Accordion
              title='Questions'
              isOpen={questionsAccordionOpen}
              onToggle={() =>
                setQuestionsAccordionOpen(!questionsAccordionOpen)
              }
              icon={<FileText className='h-5 w-5 text-blue-600' />}
              badge={`${questionData.data.length} of ${questionData.totalquestions}`}
            >
              <div className='p-4'>
                {/* Questions List */}
                {renderContent()}
                {/* Pagination */}
                {questionData?.totalpages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={questionData.totalpages}
                    onPageChange={handlePageChange}
                    isLoading={isLoading}
                  />
                )}
              </div>
            </Accordion>
          </div>
        )}

        {/* Empty State */}
        {!questionData && !isLoading && (
          <div className='p-12 text-center text-gray-500'>
            <FileText className='mx-auto mb-4 h-12 w-12 text-gray-400' />
            <h3 className='mb-2 text-lg font-medium text-gray-900'>
              No Questions Yet
            </h3>
            <p>
              Select your filters and click &quot;Search Questions&quot; to view
              the question bank.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionBankV2;
